// Checkpoint definitions for player intake/progress/milestone assessments.
// Keyed by discipline + skill level. The DB (player_assessments /
// assessment_scores, PR-ASSESS-1a) stores checkpoint_key as free text —
// this config is the single source of truth for which keys exist per
// discipline/level and how they're labeled in the UI.

export type Discipline = "batting" | "pace_bowling" | "spin_bowling";
export type SkillLevel = "beginner" | "intermediate" | "advanced";

export interface Checkpoint {
  key: string;
  label: string;
  description: string;
}

const BATTING_BEGINNER: Checkpoint[] = [
  { key: "grip", label: "Grip", description: "Hands positioned correctly on the bat handle." },
  { key: "stance", label: "Stance", description: "Balanced, side-on stance at the crease." },
  { key: "head_position", label: "Head Position", description: "Head still and level through the shot." },
  { key: "front_foot_drive", label: "Front-Foot Drive", description: "Weight transfer into a basic front-foot drive." },
  { key: "defensive_block", label: "Defensive Block", description: "Soft hands, full face of the bat on a straight block." },
];

const BATTING_INTERMEDIATE: Checkpoint[] = [
  { key: "grip", label: "Grip", description: "Hands positioned correctly on the bat handle." },
  { key: "backlift", label: "Backlift", description: "Backlift is straight and timed with the bowler's delivery." },
  { key: "stance", label: "Stance", description: "Balanced, side-on stance at the crease." },
  { key: "head_position", label: "Head Position", description: "Head still and level through the shot." },
  { key: "footwork", label: "Footwork", description: "Decisive front- or back-foot movement to the line of the ball." },
  { key: "shot_selection", label: "Shot Selection", description: "Picks the appropriate shot for line, length, and field." },
  { key: "back_foot_punch", label: "Back-Foot Punch", description: "Controlled punch off the back foot through the off side." },
  { key: "flick_shot", label: "Flick Shot", description: "Wrists work the ball through square leg off the pads." },
  { key: "step_out_drive", label: "Step-Out Drive", description: "Uses footwork to get to the pitch of the ball and drive." },
  { key: "step_out_power", label: "Step-Out Power", description: "Generates power when stepping out to attack." },
  { key: "running_between_wickets", label: "Running Between Wickets", description: "Calls early, backs up, and turns for twos cleanly." },
];

const BATTING_ADVANCED: Checkpoint[] = [
  { key: "trigger_move", label: "Trigger Move", description: "Consistent pre-delivery trigger movement into position." },
  { key: "pick_up_shot", label: "Pick-Up Shot", description: "Picks the ball up off the stumps over square leg." },
  { key: "scoop_shot", label: "Scoop Shot", description: "Controlled scoop over the keeper against pace." },
  { key: "ramp_shot", label: "Ramp Shot", description: "Angles the bat to ramp the ball over slip/gully." },
  { key: "sweep_reverse_sweep", label: "Sweep / Reverse Sweep", description: "Plays both conventional and reverse sweep with control." },
  { key: "powerplay_aggression", label: "Powerplay Aggression", description: "Capitalizes on field restrictions with controlled aggression." },
  { key: "strike_rotation", label: "Strike Rotation", description: "Finds singles and twos to keep the scoreboard moving." },
  { key: "field_awareness", label: "Field Awareness", description: "Reads field placements and adjusts shot selection." },
  { key: "mental_composure", label: "Mental Composure", description: "Stays composed under pressure and after dismissals nearby." },
];

const SPIN_BEGINNER: Checkpoint[] = [
  { key: "grip", label: "Grip", description: "Correct finger/seam grip for the stock delivery." },
  { key: "run_up", label: "Run-Up", description: "Consistent, balanced approach to the crease." },
  { key: "action", label: "Bowling Action", description: "Repeatable, side-on bowling action." },
  { key: "line", label: "Line", description: "Bowls a consistent line at the stumps or just outside off." },
  { key: "toss", label: "Toss", description: "Gives the ball appropriate flight/loop." },
];

const SPIN_INTERMEDIATE: Checkpoint[] = [
  { key: "line_length", label: "Line & Length", description: "Combines consistent line with a probing length." },
  { key: "grip", label: "Grip", description: "Correct finger/seam grip for the stock delivery." },
  { key: "alignment", label: "Alignment", description: "Body alignment supports accuracy and turn." },
  { key: "flight_dip", label: "Flight & Dip", description: "Varies flight and uses dip to deceive the batter." },
  { key: "turn", label: "Turn", description: "Generates consistent turn off the pitch." },
  { key: "second_variation", label: "Second Variation", description: "Has a reliable second delivery (e.g. arm ball/googly)." },
];

const SPIN_ADVANCED: Checkpoint[] = [
  { key: "deception", label: "Deception", description: "Disguises variations from a consistent action." },
  { key: "third_variation", label: "Third Variation", description: "Adds a third reliable variation to the arsenal." },
  { key: "reading_batter", label: "Reading the Batter", description: "Adjusts plan based on the batter's strengths/weaknesses." },
  { key: "dot_ball_discipline", label: "Dot-Ball Discipline", description: "Builds pressure with consistent dot balls." },
  { key: "field_awareness", label: "Field Awareness", description: "Bowls to the field set, exploiting gaps and traps." },
  { key: "responding_to_attack", label: "Responding to Attack", description: "Adapts plan when being targeted by the batter." },
];

const PACE_BEGINNER: Checkpoint[] = [
  { key: "run_up", label: "Run-Up", description: "Consistent, balanced approach to the crease." },
  { key: "action", label: "Bowling Action", description: "Repeatable, side-on bowling action." },
  { key: "grip", label: "Grip", description: "Correct seam grip for the stock delivery." },
  { key: "release", label: "Release", description: "Releases the ball at the right point for control." },
  { key: "follow_through", label: "Follow-Through", description: "Completes a balanced follow-through after release." },
];

const PACE_INTERMEDIATE: Checkpoint[] = [
  { key: "run_up_rhythm", label: "Run-Up Rhythm", description: "Run-up builds rhythm and pace into the crease." },
  { key: "wrist_position", label: "Wrist Position", description: "Wrist stays behind the ball for seam presentation." },
  { key: "natural_shape", label: "Natural Shape", description: "Ball moves with a consistent natural shape (in/away)." },
  { key: "length_control", label: "Length Control", description: "Hits a consistent, attacking length." },
  { key: "follow_through", label: "Follow-Through", description: "Completes a balanced follow-through after release." },
  { key: "short_ball", label: "Short Ball", description: "Bowls an effective, controlled short ball." },
];

const PACE_ADVANCED: Checkpoint[] = [
  { key: "seam_variation", label: "Seam Variation", description: "Varies seam position to move the ball both ways." },
  { key: "off_cutter", label: "Off Cutter", description: "Bowls a controlled off-cutter as a variation." },
  { key: "knuckleball", label: "Knuckleball", description: "Has a deceptive knuckleball/slower-ball variation." },
  { key: "slower_bouncer", label: "Slower Bouncer", description: "Disguises a slower-ball bouncer effectively." },
  { key: "yorker", label: "Yorker", description: "Lands yorkers consistently under pressure." },
  { key: "death_bowling_variations", label: "Death-Bowling Variations", description: "Mixes variations effectively in the death overs." },
  { key: "powerplay_bowling", label: "Powerplay Bowling", description: "Bowls effectively with the new ball and field restrictions." },
  { key: "bowling_per_field_setup", label: "Bowling to the Field", description: "Adjusts plans to match the set field." },
  { key: "fake_setup_execution", label: "Fake Setup Execution", description: "Disguises intent before executing a variation." },
  { key: "spell_management", label: "Spell Management", description: "Manages pace and intensity across a full spell." },
];

export const ASSESSMENT_CHECKPOINTS: Record<Discipline, Record<SkillLevel, Checkpoint[]>> = {
  batting: {
    beginner: BATTING_BEGINNER,
    intermediate: BATTING_INTERMEDIATE,
    advanced: BATTING_ADVANCED,
  },
  spin_bowling: {
    beginner: SPIN_BEGINNER,
    intermediate: SPIN_INTERMEDIATE,
    advanced: SPIN_ADVANCED,
  },
  pace_bowling: {
    beginner: PACE_BEGINNER,
    intermediate: PACE_INTERMEDIATE,
    advanced: PACE_ADVANCED,
  },
};

export const DISCIPLINE_LABELS: Record<Discipline, string> = {
  batting: "Batting",
  spin_bowling: "Spin Bowling",
  pace_bowling: "Pace Bowling",
};

export const SKILL_LEVEL_LABELS: Record<SkillLevel, string> = {
  beginner: "Beginner",
  intermediate: "Intermediate",
  advanced: "Advanced",
};

export function getCheckpoints(discipline: Discipline, skillLevel: SkillLevel): Checkpoint[] {
  return ASSESSMENT_CHECKPOINTS[discipline][skillLevel];
}

export function getCheckpointLabel(discipline: Discipline, skillLevel: SkillLevel, key: string): string {
  return getCheckpoints(discipline, skillLevel).find((c) => c.key === key)?.label ?? key;
}
